import { Autocomplete, Box, Checkbox, Chip, FormControlLabel, Typography } from "@mui/material";
import {
  AdminDetailUserInfoResponse,
  CareTeamMemberInfo,
  MedRoleGroupEnum,
} from "@veris-health/user-ms/lib/v1";
import React, { HTMLAttributes, useEffect, useMemo } from "react";
import { get, values } from "lodash";
import { StyledTextField } from "./SetHospitalAndCareTeamForm";

const renderOption = (
  { onClick, ...rest }: HTMLAttributes<HTMLLIElement | HTMLDivElement>,
  {
    value,
    label,
  }: { value: AdminDetailUserInfoResponse | CareTeamMemberInfo; label: string | undefined },
) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      sx={{
        cursor: "pointer",
      }}
      onClick={onClick}
      {...rest}
      key={`${value.id}`}
    >
      <Typography
        variant="body"
        ml={1}
        mr={1}
        color={(theme) => theme.veris.colors.neutrals["grey-dark"]}
      >
        {label}
      </Typography>
    </Box>
  );
};

type CareTeamMembersPickerProps = {
  handleChange: (
    value: { value: AdminDetailUserInfoResponse | CareTeamMemberInfo; label: string | undefined }[],
  ) => void;
  careTeamMembersStatus: string;
  selectedCareTeam: {
    value: AdminDetailUserInfoResponse | CareTeamMemberInfo;
    label: string | undefined;
  }[];
  careTeamMembers: AdminDetailUserInfoResponse[];
  editMode?: boolean;
};

const getOptionLabel = (name?: string, profession?: string): string => {
  if (profession) return `${name} - ${profession}`;
  return name || "";
};

const CareTeamMembersPicker = ({
  handleChange,
  careTeamMembersStatus,
  selectedCareTeam,
  careTeamMembers,
  editMode = false,
}: CareTeamMembersPickerProps): JSX.Element => {
  const [areAllBillableCareTeamMembersSelected, setAreAllBillableCareTeamMembersSelected] =
    React.useState(true);

  const billableCareTeamMembers = useMemo(() => {
    return careTeamMembers.filter((careTeamMember) => {
      return careTeamMember.user_roles?.some(
        (role) => role.role_group === MedRoleGroupEnum.Billable,
      );
    });
  }, [careTeamMembers]);

  const selectAllCareTeamMembers = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.checked) {
      const billableCareTeamMemberIds = billableCareTeamMembers.map((member) => member.id);
      const nonBillableSelectedCareTeamMembers = selectedCareTeam.filter((member) => {
        return !billableCareTeamMemberIds.includes(member.value.id);
      });
      handleChange(nonBillableSelectedCareTeamMembers);
      return;
    }
    const billableCareTeamMembersOptions =
      getBillableCareTeamMembersOptions(billableCareTeamMembers);
    handleChange(billableCareTeamMembersOptions);
  };

  useEffect(() => {
    if (careTeamMembers.length > 0) {
      const selectedMemberIds = selectedCareTeam.map((member) => member.value.id);

      setAreAllBillableCareTeamMembersSelected(
        billableCareTeamMembers.every((member) => selectedMemberIds.includes(member.id)),
      );
    }
  }, [selectedCareTeam, billableCareTeamMembers, careTeamMembers]);

  useEffect(() => {
    if (careTeamMembers.length > 0 && careTeamMembersStatus !== "loading" && !editMode) {
      const billableCareTeamMembersOptions =
        getBillableCareTeamMembersOptions(billableCareTeamMembers);
      handleChange(billableCareTeamMembersOptions);
    }
  }, [careTeamMembers, careTeamMembersStatus]);
  return careTeamMembersStatus === "loading" ? (
    <span style={{ paddingLeft: "1rem", fontSize: "1.1rem" }}>Loading...</span>
  ) : (
    <>
      <Box pb={2} pt={2.2} display="flex" justifyContent="flex-start">
        <FormControlLabel
          control={
            <Checkbox
              checked={areAllBillableCareTeamMembersSelected}
              onChange={selectAllCareTeamMembers}
            />
          }
          label="Assign All Clinical Staff"
        />
      </Box>
      <Autocomplete
        loading={careTeamMembersStatus === "loading"}
        sx={{
          borderRadius: (theme) => +theme.shape.borderRadius * 0.375,
          border: (theme) => `1px solid ${theme.veris.colors.neutrals["grey-2"]} `,
          padding: (theme) => theme.spacing(1, 1.25),
          marginY: (theme) => theme.spacing(2),
        }}
        multiple
        id="care-team-input"
        filterSelectedOptions
        isOptionEqualToValue={(option, value) => option.value.id === value.value.id}
        value={selectedCareTeam || []}
        onChange={(e, value) => {
          handleChange(value);
        }}
        style={{
          fontWeight: 400,
        }}
        options={
          careTeamMembers?.map((careTeamMember) => ({
            value: careTeamMember,
            label: getOptionLabel(careTeamMember.name, careTeamMember.profession),
          })) || []
        }
        renderOption={renderOption}
        ListboxProps={{
          style: {
            overflowY: "scroll",
            height: "200px",
          },
        }}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            placeholder="Select clinical care team member(s)"
            size="small"
            variant="standard"
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              variant="outlined"
              label={getOptionLabel(
                get(option.value, "full_name", get(option.value, "name")),
                option.value.profession,
              )}
              sx={{
                ...(theme) => theme.typography.body,
                fontWeight: (theme) => theme.typography.body,
              }}
              {...getTagProps({ index })}
            />
          ))
        }
      />
    </>
  );
};

function getBillableCareTeamMembersOptions(
  billableCareTeamMembers: AdminDetailUserInfoResponse[],
): { value: AdminDetailUserInfoResponse; label: string }[] {
  return billableCareTeamMembers.map((member) => ({
    value: member,
    label: getOptionLabel(member.name, member.profession),
  }));
}

export default CareTeamMembersPicker;
